import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { companyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: '',
    urgent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Map form data to API format
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company || null,
        service_type: formData.serviceType,
        message: formData.message,
        urgent: formData.urgent
      };

      // Submit to backend
      const response = await axios.post(`${API}/contact`, submissionData);
      
      if (response.status === 201) {
        sonnerToast.success("Quote Request Submitted", {
          description: "We'll contact you within 24 hours. For urgent matters, please call us directly.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          serviceType: '',
          message: '',
          urgent: false
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      sonnerToast.error("Submission Failed", {
        description: "There was an error submitting your request. Please try again or call us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      {/* Contact Hero */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <h1 className="page-title">Get in Touch</h1>
          <p className="page-subtitle">
            Request a quote or reach out for emergency support. We're here 24/7 to help.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="contact-grid">
          {/* Contact Information */}
          <div className="contact-info">
            <h2 className="contact-info-title">Contact Information</h2>
            <p className="contact-info-description">
              Reach out to us directly or fill out the form and we'll get back to you promptly.
            </p>

            <div className="contact-details">
              <a href={`tel:${companyInfo.phone}`} className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Phone size={24} />
                </div>
                <div className="contact-detail-content">
                  <h3 className="contact-detail-label">Phone</h3>
                  <p className="contact-detail-value">{companyInfo.phone}</p>
                </div>
              </a>

              <a href={`mailto:${companyInfo.email}`} className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Mail size={24} />
                </div>
                <div className="contact-detail-content">
                  <h3 className="contact-detail-label">Email</h3>
                  <p className="contact-detail-value">{companyInfo.email}</p>
                </div>
              </a>

              <div className="contact-detail-item">
                <div className="contact-detail-icon">
                  <MapPin size={24} />
                </div>
                <div className="contact-detail-content">
                  <h3 className="contact-detail-label">Location</h3>
                  <p className="contact-detail-value">{companyInfo.location}</p>
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Clock size={24} />
                </div>
                <div className="contact-detail-content">
                  <h3 className="contact-detail-label">Hours</h3>
                  <p className="contact-detail-value">{companyInfo.hours}</p>
                </div>
              </div>
            </div>

            <div className="emergency-notice">
              <h3 className="emergency-notice-title">Emergency Support</h3>
              <p className="emergency-notice-text">
                For critical P1/P0 incidents, call us immediately at{' '}
                <a href={`tel:${companyInfo.phone}`} className="emergency-phone">
                  {companyInfo.phone}
                </a>
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-container">
            <h2 className="contact-form-title">Request a Quote</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Your name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="(123) 456-7890"
                />
              </div>

              <div className="form-group">
                <label htmlFor="company" className="form-label">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Company name (optional)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="serviceType" className="form-label">Service Needed *</label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className="form-select"
                >
                  <option value="">Select a service</option>
                  <option value="network-troubleshooting">Network Troubleshooting</option>
                  <option value="onsite-support">On-Site IT Support</option>
                  <option value="router-replacement">Router/AP Replacement</option>
                  <option value="datacenter">Data Center Dispatch</option>
                  <option value="cable-installation">Cable Installation & Testing</option>
                  <option value="installations">Installations & Configurations</option>
                  <option value="emergency">Emergency Incident Response</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group form-group-full">
                <label htmlFor="message" className="form-label">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="form-textarea"
                  placeholder="Please describe your requirements or issue..."
                />
              </div>

              <div className="form-group form-group-full">
                <label className="form-checkbox-label">
                  <input
                    type="checkbox"
                    name="urgent"
                    checked={formData.urgent}
                    onChange={handleChange}
                    className="form-checkbox"
                  />
                  <span className="form-checkbox-text">This is an urgent request</span>
                </label>
              </div>

              <button type="submit" className="btn-primary form-submit" disabled={isSubmitting}>
                <Send size={20} />
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
